import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import { collection, onSnapshot, addDoc, serverTimestamp, deleteDoc, doc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import {
  Box, Typography, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, IconButton,
  TextField, InputAdornment, CircularProgress, Collapse,
  Grid, Divider, Button, TableSortLabel, Tooltip,
  FormGroup, FormControlLabel, Checkbox, Accordion,
  AccordionSummary, AccordionDetails
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import FolderIcon from "@mui/icons-material/Folder";
import AssessmentIcon from "@mui/icons-material/Assessment";
import FilterListIcon from "@mui/icons-material/FilterList";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LinkIcon from "@mui/icons-material/Link";

const STATO_COLORI = {
  qualificazione: "warning",
  preventivo: "default",
  chiusoAcquisito: "primary",
  chiusoPerso: "error",
  completato: "success",
  annullato: "error",
  rimandato: "warning",
  fatturato: "secondary",
  archiviato: "default",
};

const STATO_LABEL = {
  qualificazione: "Qualificazione",
  preventivo: "Preventivo",
  chiusoAcquisito: "Chiuso Acquisito",
  chiusoPerso: "Chiuso Perso",
  completato: "Completato",
  annullato: "Annullato",
  rimandato: "Rimandato",
  fatturato: "Fatturato",
  archiviato: "Archiviato",
};

const STATI_DEFAULT_VISIBILI = ["qualificazione", "preventivo", "chiusoAcquisito", "completato", "annullato", "rimandato", "fatturato"];

function RigaEvento({ evento, clienti, tipologieMap, tecnologieMap, piattaformeStudioMap, piattaformeDistrMap, navigate }) {
  const [aperta, setAperta] = useState(false);
  const [copiato, setCopiato] = useState(false);
  const nomeCliente = clienti[evento.clienteId] || evento.clienteNomeStorico || "—";

  async function handleDuplica(e) {
    e.stopPropagation();
    if (!window.confirm(`Vuoi duplicare l'evento "${evento.titolo}"? Verrà creato un nuovo evento in stato Qualificazione.`)) return;

    const { id, cartellaDrive, idEventiCalendar, createdAt, updatedAt, stato,
      opportunitaCRMId, taskFatturazioneId, accountCRMId, opportunitaCRM,
      mailupListaId, mailupInvitoId, mailupReminderId, mailupPromemoriaId,
      mailupGruppoTuttiId, mailupGruppoConfermaId, mailupGruppoNoId, ...datiEvento } = evento;

    await addDoc(collection(db, "eventi"), {
      ...datiEvento,
      titolo: `${evento.titolo} (copia)`,
      stato: "qualificazione",
      cartellaDrive: "",
      idEventiCalendar: {},
      opportunitaCRMId: "",
      taskFatturazioneId: "",
      accountCRMId: "",
      opportunitaCRM: "",
      mailupListaId: "",
      mailupInvitoId: "",
      mailupReminderId: "",
      mailupPromemoriaId: "",
      mailupGruppoTuttiId: "",
      mailupGruppoConfermaId: "",
      mailupGruppoNoId: "",
      createdAt: serverTimestamp(),
    });
  }

  async function handleElimina(e) {
    e.stopPropagation();
    if (!window.confirm(`Vuoi eliminare definitivamente l'evento "${evento.titolo}"? L'operazione è irreversibile.`)) return;
    try {
      await deleteDoc(doc(db, "eventi", evento.id));
    } catch (err) {
      console.error("Errore eliminazione:", err);
    }
  }

  function handleCopiaLink() {
    navigator.clipboard.writeText(`https://dge.algores.it/accesso/${evento.id}`);
    setCopiato(true);
    setTimeout(() => setCopiato(false), 2000);
  }

  return (
    <>
      <TableRow
        hover
        sx={{ cursor: "pointer", opacity: evento.stato === "archiviato" ? 0.6 : 1 }}
        onClick={() => setAperta(!aperta)}
      >
        <TableCell sx={{ width: 40, padding: "0 8px" }}>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); setAperta(!aperta); }}>
            {aperta ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{nomeCliente}</TableCell>
        <TableCell>{evento.titolo || "—"}</TableCell>
        <TableCell>{evento.dataInizio || evento.data || "—"}</TableCell>
        <TableCell>
          <Chip
            label={STATO_LABEL[evento.stato] || evento.stato}
            color={STATO_COLORI[evento.stato] || "default"}
            size="small"
            variant={evento.stato === "archiviato" ? "outlined" : "filled"}
          />
        </TableCell>
        <TableCell align="center" onClick={(e) => e.stopPropagation()}>
          <Tooltip title="Report presenze">
            <IconButton size="small" onClick={() => navigate(`/evento/${evento.id}/report-presenze`)}>
              <AssessmentIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Duplica evento">
            <IconButton size="small" onClick={handleDuplica}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Elimina evento">
            <IconButton size="small" color="error" onClick={handleElimina}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Modifica evento">
            <IconButton size="small" onClick={() => navigate(`/evento/${evento.id}`)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={6} sx={{ py: 0, backgroundColor: "#fafafa" }}>
          <Collapse in={aperta} timeout="auto" unmountOnExit>
            <Box sx={{ p: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" color="text.secondary">Tipo evento</Typography>
                  <Typography variant="body2">{evento.tipoEvento || "—"}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" color="text.secondary">Ora inizio</Typography>
                  <Typography variant="body2">{evento.oraInizio || "—"}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" color="text.secondary">Data fine</Typography>
                  <Typography variant="body2">{evento.dataFine || "—"}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" color="text.secondary">Ora fine</Typography>
                  <Typography variant="body2">{evento.oraFine || "—"}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" color="text.secondary">Durata (ore)</Typography>
                  <Typography variant="body2">{evento.durata || "—"}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" color="text.secondary">Durata (giorni)</Typography>
                  <Typography variant="body2">{evento.durataGiorni || "—"}</Typography>
                </Grid>
                <Grid item xs={12}><Divider /></Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Descrizione</Typography>
                  <Typography variant="body2">{evento.descrizione || "—"}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Tipologia evento</Typography>
                  <Typography variant="body2">
                    {Array.isArray(evento.tipologiaEvento)
                      ? evento.tipologiaEvento.map((id) => tipologieMap[id] || id).join(", ")
                      : evento.tipologiaEvento || "—"}
                  </Typography>
                </Grid>
                <Grid item xs={12}><Divider /></Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" color="text.secondary">Tecnologia</Typography>
                  <Typography variant="body2">
                    {Array.isArray(evento.tecnologie)
                      ? evento.tecnologie.map((id) => tecnologieMap[id] || id).join(", ")
                      : "—"}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" color="text.secondary">Piattaforma studio</Typography>
                  <Typography variant="body2">
                    {piattaformeStudioMap[evento.piattaformaStudio] || evento.piattaformaStudio || "—"}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" color="text.secondary">Piattaforma distribuzione</Typography>
                  <Typography variant="body2">
                    {piattaformeDistrMap[evento.piattaformaDistribuzione] || evento.piattaformaDistribuzione || "—"}
                  </Typography>
                </Grid>
                <Grid item xs={12}><Divider /></Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="caption" color="text.secondary">Chat attiva</Typography>
                  <Typography variant="body2">{evento.chatAttiva ? "Sì" : "No"}</Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="caption" color="text.secondary">D&R attiva</Typography>
                  <Typography variant="body2">{evento.drAttiva ? "Sì" : "No"}</Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="caption" color="text.secondary">Survey post-evento</Typography>
                  <Typography variant="body2">{evento.surveyPostEvento ? "Sì" : "No"}</Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="caption" color="text.secondary">DEM attiva</Typography>
                  <Typography variant="body2">{evento.demAttiva ? "Sì" : "No"}</Typography>
                </Grid>
                <Grid item xs={12}><Divider /></Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" color="text.secondary">Importo di vendita</Typography>
                  <Typography variant="body2">
                    {evento.importoVendita ? `€ ${evento.importoVendita}` : "—"}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" color="text.secondary">Ore Algores</Typography>
                  <Typography variant="body2">{evento.oreAlgores || "—"}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" color="text.secondary">Ore Collaboratori</Typography>
                  <Typography variant="body2">{evento.oreCollaboratori || "—"}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" color="text.secondary">Numero fattura</Typography>
                  <Typography variant="body2">{evento.numFattura || "—"}</Typography>
                </Grid>
                {evento.cartellaDrive && (
                  <>
                    <Grid item xs={12}><Divider /></Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">Cartella Drive</Typography>
                      <Box mt={0.5}>
                        <Button variant="outlined" size="small" startIcon={<FolderIcon />}
                          href={evento.cartellaDrive} target="_blank" rel="noopener noreferrer">
                          Apri cartella
                        </Button>
                      </Box>
                    </Grid>
                  </>
                )}
                {["chiusoAcquisito", "completato", "fatturato", "archiviato"].includes(evento.stato) && (
                  <>
                    <Grid item xs={12}><Divider /></Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">Link dashboard cliente</Typography>
                      <Box mt={0.5} display="flex" alignItems="center" gap={1} flexWrap="wrap">
                        <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: 12, color: "text.secondary" }}>
                          https://dge.algores.it/accesso/{evento.id}
                        </Typography>
                        <Button size="small" variant="outlined" startIcon={<LinkIcon />}
                          onClick={handleCopiaLink}>
                          {copiato ? "Copiato!" : "Copia link"}
                        </Button>
                      </Box>
                    </Grid>
                  </>
                )}
                {evento.motivoAnnullamento && (
                  <>
                    <Grid item xs={12}><Divider /></Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">Motivo annullamento/rinvio</Typography>
                      <Typography variant="body2">{evento.motivoAnnullamento}</Typography>
                    </Grid>
                  </>
                )}
              </Grid>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function ListaEventi() {
  const navigate = useNavigate();
  const [eventi, setEventi] = useState([]);
  const [clienti, setClienti] = useState({});
  const [tipologieMap, setTipologieMap] = useState({});
  const [tecnologieMap, setTecnologieMap] = useState({});
  const [piattaformeStudioMap, setPiattaformeStudioMap] = useState({});
  const [piattaformeDistrMap, setPiattaformeDistrMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [ricerca, setRicerca] = useState("");
  const [ordinamento, setOrdinamento] = useState({ campo: "dataInizio", direzione: "desc" });
  const [statiVisibili, setStatiVisibili] = useState(
    Object.fromEntries(Object.keys(STATO_LABEL).map((s) => [s, STATI_DEFAULT_VISIBILI.includes(s)]))
  );

  useEffect(() => {
    const unsubClienti = onSnapshot(collection(db, "clienti"), (snap) => {
      const map = {};
      snap.docs.forEach((doc) => { map[doc.id] = doc.data().nome; });
      setClienti(map);
    });
    const unsubEventi = onSnapshot(collection(db, "eventi"), (snap) => {
      const eventiCaricati = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setEventi(eventiCaricati);
      setLoading(false);
    });
    const unsubTipologie = onSnapshot(collection(db, "tipologieEvento"), (snap) => {
      const map = {};
      snap.docs.forEach((doc) => { map[doc.id] = doc.data().nome; });
      setTipologieMap(map);
    });
    const unsubTecnologie = onSnapshot(collection(db, "tecnologie"), (snap) => {
      const map = {};
      snap.docs.forEach((doc) => { map[doc.id] = doc.data().nome; });
      setTecnologieMap(map);
    });
    const unsubStudio = onSnapshot(collection(db, "piattaformeStudio"), (snap) => {
      const map = {};
      snap.docs.forEach((doc) => { map[doc.id] = doc.data().nome; });
      setPiattaformeStudioMap(map);
    });
    const unsubDistr = onSnapshot(collection(db, "piattaformeDistribuzione"), (snap) => {
      const map = {};
      snap.docs.forEach((doc) => { map[doc.id] = doc.data().nome; });
      setPiattaformeDistrMap(map);
    });

    return () => {
      unsubClienti(); unsubEventi();
      unsubTipologie(); unsubTecnologie();
      unsubStudio(); unsubDistr();
    };
  }, []);

  function handleOrdinamento(campo) {
    setOrdinamento((prev) => ({
      campo,
      direzione: prev.campo === campo && prev.direzione === "asc" ? "desc" : "asc",
    }));
  }

  function toggleStato(stato) {
    setStatiVisibili((prev) => ({ ...prev, [stato]: !prev[stato] }));
  }

  function getValoreOrdinamento(evento, campo) {
    switch (campo) {
      case "cliente": return clienti[evento.clienteId] || evento.clienteNomeStorico || "";
      case "titolo": return evento.titolo || "";
      case "dataInizio": return evento.dataInizio || evento.data || "";
      case "stato": return evento.stato || "";
      default: return "";
    }
  }

  const statiAttivi = Object.entries(statiVisibili).filter(([_, v]) => v).map(([k]) => k);

  const eventiFiltrati = eventi
    .filter((e) => {
      const cliente = clienti[e.clienteId] || e.clienteNomeStorico || "";
      const titolo = e.titolo || "";
      const s = ricerca.toLowerCase();
      const matchRicerca = cliente.toLowerCase().includes(s) || titolo.toLowerCase().includes(s);
      const matchStato = statiAttivi.includes(e.stato);
      return matchRicerca && matchStato;
    })
    .sort((a, b) => {
      const valA = getValoreOrdinamento(a, ordinamento.campo);
      const valB = getValoreOrdinamento(b, ordinamento.campo);
      const dir = ordinamento.direzione === "asc" ? 1 : -1;
      return valA.localeCompare(valB, "it") * dir;
    });

  if (loading) return <Box display="flex" justifyContent="center" mt={8}><CircularProgress /></Box>;

  return (
    <Box>
      <Typography variant="h5" fontWeight="bold" mb={3}>Lista Eventi</Typography>

      <Box display="flex" gap={2} mb={2} alignItems="flex-start">
        <TextField
          fullWidth
          placeholder="Cerca per cliente o titolo..."
          value={ricerca}
          onChange={(e) => setRicerca(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
        />
      </Box>

      <Accordion elevation={0} sx={{ mb: 2, border: "1px solid #e0e0e0", borderRadius: 1 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" alignItems="center" gap={1}>
            <FilterListIcon fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">
              Filtro stati ({statiAttivi.length}/{Object.keys(STATO_LABEL).length} visibili)
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <FormGroup row>
            {Object.entries(STATO_LABEL).map(([stato, label]) => (
              <FormControlLabel
                key={stato}
                control={
                  <Checkbox
                    checked={statiVisibili[stato]}
                    onChange={() => toggleStato(stato)}
                    size="small"
                  />
                }
                label={
                  <Chip
                    label={label}
                    size="small"
                    color={STATO_COLORI[stato] || "default"}
                    variant={statiVisibili[stato] ? "filled" : "outlined"}
                  />
                }
              />
            ))}
          </FormGroup>
        </AccordionDetails>
      </Accordion>

      <TableContainer component={Paper} elevation={1}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f0f4ff" }}>
              <TableCell sx={{ width: 40 }} />
              <TableCell sx={{ width: 180 }}>
                <TableSortLabel
                  active={ordinamento.campo === "cliente"}
                  direction={ordinamento.campo === "cliente" ? ordinamento.direzione : "asc"}
                  onClick={() => handleOrdinamento("cliente")}
                >
                  <strong>Cliente</strong>
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ width: "35%" }}>
                <TableSortLabel
                  active={ordinamento.campo === "titolo"}
                  direction={ordinamento.campo === "titolo" ? ordinamento.direzione : "asc"}
                  onClick={() => handleOrdinamento("titolo")}
                >
                  <strong>Titolo</strong>
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ width: 130 }}>
                <TableSortLabel
                  active={ordinamento.campo === "dataInizio"}
                  direction={ordinamento.campo === "dataInizio" ? ordinamento.direzione : "asc"}
                  onClick={() => handleOrdinamento("dataInizio")}
                >
                  <strong>Data</strong>
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ width: 120 }}>
                <TableSortLabel
                  active={ordinamento.campo === "stato"}
                  direction={ordinamento.campo === "stato" ? ordinamento.direzione : "asc"}
                  onClick={() => handleOrdinamento("stato")}
                >
                  <strong>Stato</strong>
                </TableSortLabel>
              </TableCell>
              <TableCell align="center"><strong>Azioni</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {eventiFiltrati.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  Nessun evento trovato
                </TableCell>
              </TableRow>
            ) : (
              eventiFiltrati.map((evento) => (
                <RigaEvento
                  key={evento.id}
                  evento={evento}
                  clienti={clienti}
                  tipologieMap={tipologieMap}
                  tecnologieMap={tecnologieMap}
                  piattaformeStudioMap={piattaformeStudioMap}
                  piattaformeDistrMap={piattaformeDistrMap}
                  navigate={navigate}
                />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}